export const getMicrosoftOAuthRedirectUri = (oauthRedirectUri, apiBaseUrl) => {
  const fromApi =
    typeof oauthRedirectUri === "string" ? oauthRedirectUri.trim() : "";

  if (fromApi) {
    return fromApi;
  }

  const base = (apiBaseUrl || "").replace(/\/$/, "");

  if (!base) {
    return "";
  }

  return `${base}/mailer_setting/oauth/microsoft/callback`;
};

export const FALLBACK_PROVIDER_LABELS = {
  microsoft: "Microsoft 365 / Outlook",
  google: "Google / Gmail",
  yahoo: "Yahoo",
  custom: "Other / Custom",
};

export const FALLBACK_PRESETS = {
  microsoft: {
    smtpServer: "smtp.office365.com",
    smtpPort: 587,
    incomingServer: "outlook.office365.com",
    incomingPort: 993,
    incomingEncryption: "SSL",
  },
  google: {
    smtpServer: "smtp.gmail.com",
    smtpPort: 587,
    incomingServer: "imap.gmail.com",
    incomingPort: 993,
    incomingEncryption: "SSL",
  },
  yahoo: {
    smtpServer: "smtp.mail.yahoo.com",
    smtpPort: 587,
    incomingServer: "imap.mail.yahoo.com",
    incomingPort: 993,
    incomingEncryption: "SSL",
  },
  custom: {},
};

export const INCOMING_ENCRYPTION_OPTIONS = ["SSL", "TLS", "STARTTLS", "NONE"];

export const AUTH_MODE_OUT_OPTIONS = [
  { value: "PASSWORD", labelKey: "Password" },
  { value: "APP_PASSWORD", labelKey: "App Password" },
];

export const defaultAuthModeOut = (provider) =>
  provider === "google" || provider === "yahoo" ? "APP_PASSWORD" : "PASSWORD";

export const PROVIDER_ORDER = ["microsoft", "google", "yahoo", "custom"];
export const isMicrosoftProvider = (provider) => provider === "microsoft";
export const isCustomProvider = (provider) => provider === "custom";
export const isOtherProvider = (provider) => !isMicrosoftProvider(provider);

const assignIfPresent = (target, key, value) => {
  if (value === undefined || value === null) {
    return;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }
    target[key] = trimmed;
    return;
  }

  target[key] = value;
};

export const extractApiMessage = (errorOrData) => {
  const data = errorOrData?.response?.data || errorOrData;
  const message = data?.message;

  if (typeof message === "string" && message) {
    return message;
  }

  if (message?.description) {
    return message.description;
  }

  if (message && typeof message === "object") {
    const firstKey = Object.keys(message)[0];
    const first = message[firstKey];

    if (typeof first === "string") {
      return first;
    }

    if (Array.isArray(first) && first[0]) {
      return first[0];
    }

    if (first && typeof first === "object") {
      const nested = Object.values(first)[0];
      if (typeof nested === "string") {
        return nested;
      }
      if (Array.isArray(nested) && nested[0]) {
        return nested[0];
      }
    }
  }

  if (typeof data?.error === "string") {
    return data.error;
  }

  if (errorOrData?.message && typeof errorOrData.message === "string") {
    return errorOrData.message;
  }

  return null;
};

export const isIncomingValidateUnavailable = (errorOrData) => {
  const data = errorOrData?.response?.data || errorOrData;
  const status = errorOrData?.response?.status || data?.httpStatus;
  const text = String(data?.error || extractApiMessage(errorOrData) || "");

  return status === 404 || /404 page not found/i.test(text);
};

export const isConnectMicrosoftHint = (text) => {
  if (!text || typeof text !== "string") {
    return false;
  }

  return /connect microsoft/i.test(text);
};

export const mapMicrosoftOAuthMessage = (raw, t) => {
  if (!raw) {
    return null;
  }

  const text = String(raw);

  if (/MAILER_MS_REDIRECT_URI|not configured on the server/i.test(text)) {
    return t(
      "This is a SIP server configuration issue. Do not invent a Redirect URI."
    );
  }

  if (/invalid_client|secret id/i.test(text)) {
    return t("Use Client secret Value, not Secret ID (UUID).");
  }

  if (/Invalid or expired OAuth state/i.test(text)) {
    return t("Click Connect Microsoft again.");
  }

  if (/Entra App Registration is missing/i.test(text)) {
    return t(
      "Re-save Tenant ID, Client ID and Client secret, then connect."
    );
  }

  if (
    /before connecting Microsoft|Entra app is incomplete|Save your organization Entra/i.test(
      text
    )
  ) {
    return t("Save Tenant ID, Client ID and Client secret, then connect.");
  }

  if (/404 page not found/i.test(text)) {
    return t(
      "SMTP works. Inbox test is missing on the server (404). This is not an Entra permission issue."
    );
  }

  if (/IMAP OAuth failed/i.test(text)) {
    return t(
      "Microsoft IMAP OAuth failed. Reconnect and check IMAP permission plus admin consent."
    );
  }

  if (/access_denied/i.test(text)) {
    return t("Microsoft sign-in was cancelled or access was denied.");
  }

  return null;
};

export const mapMailerSettingsMessage = (raw, t) => {
  const oauthMessage = mapMicrosoftOAuthMessage(raw, t);

  if (oauthMessage) {
    return oauthMessage;
  }

  if (!raw) {
    return null;
  }

  const text = String(raw);

  if (/SMTP server, port and username are required/i.test(text)) {
    return t("Pick a provider or fill hosts.");
  }

  if (/SMTP OK, but IMAP failed/i.test(text)) {
    return t("Fix IMAP password / app password / host.");
  }

  if (/incoming mailer settings not available/i.test(text)) {
    return t("Configure and activate Email settings first.");
  }

  if (
    /connection reset by peer|cannot reach smtp|smtp.*(fail|auth|timeout)|authentication failed/i.test(
      text
    )
  ) {
    return t("Cannot reach SMTP. Check password / firewall. Save is still allowed.");
  }

  return null;
};

const MAILER_FORM_FIELDS = [
  "emailAddress",
  "displayName",
  "smtpSecret",
  "smtpServer",
  "smtpPort",
  "smtpUsername",
  "incomingServer",
  "incomingPort",
  "incomingEncryption",
  "incomingUsername",
  "incomingSecret",
  "oauthTenantId",
  "oauthClientId",
  "oauthClientSecret",
  "authModeOut",
];

const MAILER_FORM_FIELD_ALIASES = {
  email_address: "emailAddress",
  display_name: "displayName",
  smtp_secret: "smtpSecret",
  smtp_server: "smtpServer",
  smtp_port: "smtpPort",
  smtp_username: "smtpUsername",
  incoming_server: "incomingServer",
  incoming_port: "incomingPort",
  incoming_encryption: "incomingEncryption",
  incoming_username: "incomingUsername",
  incoming_secret: "incomingSecret",
  oauth_tenant_id: "oauthTenantId",
  oauth_client_id: "oauthClientId",
  oauth_client_secret: "oauthClientSecret",
  auth_mode_out: "authModeOut",
};

const MAILER_HOST_FIELDS = [
  "smtpServer",
  "smtpPort",
  "smtpUsername",
  "incomingServer",
  "incomingPort",
  "incomingEncryption",
  "incomingUsername",
];

const firstFormErrorText = (value) => {
  if (typeof value === "string" && value) {
    return value;
  }

  if (Array.isArray(value) && value[0]) {
    return firstFormErrorText(value[0]);
  }

  if (value && typeof value === "object") {
    return firstFormErrorText(Object.values(value)[0]);
  }

  return null;
};

const normalizeMailerFormField = (key) => {
  const last = String(key || "").split(".").pop();

  if (MAILER_FORM_FIELDS.includes(last)) {
    return last;
  }

  return MAILER_FORM_FIELD_ALIASES[last] || null;
};

const collectMailerFormErrors = (source, result) => {
  if (!source || typeof source !== "object") {
    return;
  }

  Object.entries(source).forEach(([key, value]) => {
    const field = normalizeMailerFormField(key);

    if (field) {
      const text = firstFormErrorText(value);

      if (text && !result[field]) {
        result[field] = text;
      }

      return;
    }

    if (value && typeof value === "object") {
      collectMailerFormErrors(value, result);
    }
  });
};

export const extractMailerFormErrors = (errorOrData) => {
  const data = errorOrData?.response?.data || errorOrData;
  const result = {};
  const message = data?.message;

  if (message && typeof message === "object") {
    collectMailerFormErrors(message, result);
  }

  if (data?.details && typeof data.details === "object") {
    collectMailerFormErrors(data.details, result);
  }

  return result;
};

export const hasMailerHostFormErrors = (fields = {}) =>
  MAILER_HOST_FIELDS.some((field) => !!fields[field]);

export const hasIncomingSecretFormError = (fields = {}) => !!fields.incomingSecret;

export const withIncompleteEntraFormErrors = (fields = {}, raw, t) => {
  const text = String(raw || "");

  if (
    !/Entra app is incomplete|Send oauthTenantId|Save your organization Entra/i.test(
      text
    )
  ) {
    return fields;
  }

  const next = { ...fields };
  const message = t("This field cannot be blank");

  ["oauthTenantId", "oauthClientId", "oauthClientSecret"].forEach((field) => {
    if (!next[field]) {
      next[field] = message;
    }
  });

  return next;
};

export const applyMailerFormErrors = (form, fields = {}) => {
  if (!form || typeof form.setError !== "function") {
    return;
  }

  Object.entries(fields).forEach(([name, message]) => {
    form.setError(name, true, message);
  });
};

const asPresetItem = (item = {}) => ({
  smtpServer: item.smtpServer || item.smtp_server,
  smtpPort: item.smtpPort || item.smtp_port,
  incomingServer: item.incomingServer || item.incoming_server || item.imapServer,
  incomingPort: item.incomingPort || item.incoming_port || item.imapPort,
  incomingEncryption: (
    item.incomingEncryption ||
    item.incoming_encryption ||
    item.imapEncryption ||
    ""
  )
    .toString()
    .toUpperCase(),
  label: item.label || item.name,
});

export const normalizePresets = (raw) => {
  const result = {};

  PROVIDER_ORDER.forEach((key) => {
    result[key] = {
      provider: key,
      label: FALLBACK_PROVIDER_LABELS[key],
      ...FALLBACK_PRESETS[key],
    };
  });

  if (!raw) {
    return result;
  }

  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw.providers)
    ? raw.providers
    : Array.isArray(raw.presets)
    ? raw.presets
    : Array.isArray(raw.items)
    ? raw.items
    : null;

  const applyItem = (key, item) => {
    if (!key || !result[key] || !item || typeof item !== "object") {
      return;
    }

    const parsed = asPresetItem(item);
    result[key] = {
      ...result[key],
      ...Object.fromEntries(
        Object.entries(parsed).filter(([, value]) => value !== undefined && value !== "")
      ),
      provider: key,
      label: parsed.label || result[key].label,
    };
  };

  if (list) {
    list.forEach((item) => {
      const key = (item?.provider || item?.code || item?.key || "")
        .toString()
        .toLowerCase();
      applyItem(key, item);
    });
    return result;
  }

  const map = raw.presets && !Array.isArray(raw.presets) ? raw.presets : raw;
  Object.keys(map || {}).forEach((key) => {
    applyItem(key.toLowerCase(), map[key]);
  });

  return result;
};

export const pickHostFields = (preset = {}) => {
  const hosts = {};
  assignIfPresent(hosts, "smtpServer", preset.smtpServer);
  assignIfPresent(hosts, "smtpPort", preset.smtpPort);
  assignIfPresent(hosts, "incomingServer", preset.incomingServer);
  assignIfPresent(hosts, "incomingPort", preset.incomingPort);
  assignIfPresent(hosts, "incomingEncryption", preset.incomingEncryption);
  return hosts;
};

export const shouldSyncUsernameWithEmail = (username, previousEmail) => {
  const current = String(username || "").trim();
  const previous = String(previousEmail || "").trim();

  return !current || current === previous;
};

const unwrapSaveData = (payload) => {
  if (payload?.data && typeof payload.data === "object") {
    return payload.data;
  }

  return payload || {};
};

export const getMailboxAccountChange = (payload) => {
  const nested = unwrapSaveData(payload);
  const required =
    payload?.mailboxAccountChangeRequired === true ||
    nested.mailboxAccountChangeRequired === true;
  const change =
    payload?.mailboxAccountChange || nested.mailboxAccountChange;

  if (!required || !change || typeof change !== "object") {
    return null;
  }

  return {
    previousEmail: change.previousEmail || "",
    newEmail: change.newEmail || "",
    previousIncomingUsername: change.previousIncomingUsername || "",
    newIncomingUsername: change.newIncomingUsername || "",
    reused: !!change.reused,
    warning: change.warning || "",
  };
};

export const getMailboxAccountConfirmResult = (payload) => {
  const nested = unwrapSaveData(payload);
  const reusedRaw =
    payload?.mailboxAccountReused ?? nested.mailboxAccountReused;

  return {
    changed:
      payload?.mailboxAccountChanged === true ||
      nested.mailboxAccountChanged === true,
    reused: reusedRaw === undefined ? null : !!reusedRaw,
  };
};

export const buildMailerPayload = (
  formValues = {},
  {
    provider,
    imapSyncEnabled,
    incomingSameAsSmtp,
    confirmMailboxChange,
    fallbacks = {},
  }
) => {
  const emailAddress = (formValues.emailAddress || "").trim();
  const mailer_setting = {};
  const valueOf = (key) => {
    const fromForm = formValues[key];
    if (fromForm !== undefined && fromForm !== null && String(fromForm).trim() !== "") {
      return fromForm;
    }

    return fallbacks[key];
  };

  assignIfPresent(mailer_setting, "emailAddress", emailAddress);
  assignIfPresent(mailer_setting, "displayName", formValues.displayName);
  assignIfPresent(
    mailer_setting,
    "smtpUsername",
    valueOf("smtpUsername") || emailAddress
  );
  assignIfPresent(mailer_setting, "smtpSecret", formValues.smtpSecret);
  mailer_setting.imapSyncEnabled = !!imapSyncEnabled;
  mailer_setting.authModeOut =
    (valueOf("authModeOut") || "").toString().trim() ||
    defaultAuthModeOut(provider);

  assignIfPresent(mailer_setting, "smtpServer", valueOf("smtpServer"));
  assignIfPresent(mailer_setting, "smtpPort", valueOf("smtpPort"));
  assignIfPresent(mailer_setting, "incomingServer", valueOf("incomingServer"));
  assignIfPresent(mailer_setting, "incomingPort", valueOf("incomingPort"));
  assignIfPresent(
    mailer_setting,
    "incomingEncryption",
    valueOf("incomingEncryption")
      ? String(valueOf("incomingEncryption")).toUpperCase()
      : undefined
  );

  if (isMicrosoftProvider(provider)) {
    assignIfPresent(mailer_setting, "oauthTenantId", valueOf("oauthTenantId"));
    assignIfPresent(mailer_setting, "oauthClientId", valueOf("oauthClientId"));
    assignIfPresent(
      mailer_setting,
      "oauthClientSecret",
      formValues.oauthClientSecret
    );
  } else {
    assignIfPresent(
      mailer_setting,
      "incomingUsername",
      valueOf("incomingUsername") || emailAddress
    );
    const incomingSecret = incomingSameAsSmtp
      ? formValues.smtpSecret
      : formValues.incomingSecret;
    assignIfPresent(mailer_setting, "incomingSecret", incomingSecret);
  }

  const payload = {
    provider,
    enableIncoming: !!imapSyncEnabled,
    mailer_setting,
  };

  if (confirmMailboxChange) {
    payload.confirmMailboxChange = true;
  }

  return payload;
};
